import { Link } from "react-router-dom";
import { QrCode, Palette, Download, Wifi, Mail, MessageSquare, Link2, Phone, Globe } from "lucide-react";
import { SeoHead } from "@/components/SeoHead";
import { Button } from "@/components/ui/button";
import { SEO_ROUTES } from "@/config/seo-routes";
import { PAGE_SEO } from "@/config/seo-pages";

const qrTypes = [
  { icon: Link2, label: "URL", desc: "Link a qualsiasi sito web" },
  { icon: Wifi, label: "WiFi", desc: "Condividi reti WiFi" },
  { icon: Mail, label: "Email", desc: "Invia email precompilate" },
  { icon: Phone, label: "Telefono", desc: "Chiama con un tap" },
  { icon: MessageSquare, label: "SMS", desc: "Messaggi precompilati" },
  { icon: Globe, label: "Testo", desc: "Qualsiasi testo libero" },
];

const features = [
  {
    icon: Palette,
    title: "Personalizzazione Totale",
    desc: "Colori, forme dei dots, angoli arrotondati, gradiente e molto altro.",
  },
  {
    icon: QrCode,
    title: "Logo al Centro",
    desc: "Carica il tuo logo e posizionalo al centro del QR code.",
  },
  {
    icon: Download,
    title: "Download HD",
    desc: "Scarica in PNG o SVG ad alta risoluzione, pronto per la stampa.",
  },
];

const Index = () => {
  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        title={PAGE_SEO.home.title}
        description={PAGE_SEO.home.description}
        canonical="/"
      />

      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="container flex h-16 items-center justify-between">
          <div className="flex items-center gap-2">
            <QrCode className="h-7 w-7 text-primary" />
            <span className="text-xl font-bold gradient-text">QRGratis</span>
          </div>
          <Link to={SEO_ROUTES.generator}>
            <Button className="gradient-purple border-0 text-primary-foreground font-semibold glow-purple">
              Crea QR Code
            </Button>
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section
        className="relative flex min-h-[90vh] items-center justify-center overflow-hidden pt-16"
        style={{ backgroundImage: "url(/hero-bg.jpg)", backgroundSize: "cover", backgroundPosition: "center" }}
      >
        <div className="absolute inset-0 bg-background/60" />
        <div className="container relative z-10 text-center py-20">
          <div className="inline-block mb-6 rounded-full border border-primary/40 bg-primary/25 px-4 py-1.5 text-sm text-primary-foreground">
            ✨ Generatore QR Code Gratuito
          </div>
          <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-6">
            {PAGE_SEO.home.h1.replace(" Gratis", "")}{" "}
            <span className="gradient-text">Gratis</span>
          </h1>
          <p className="mx-auto max-w-2xl text-lg md:text-xl text-muted-foreground mb-10">
            Il generatore di QR code più personalizzabile. Scegli colori, forme, logo e scarica in alta qualità. Gratis, veloce, senza limiti.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to={SEO_ROUTES.generator}>
              <Button size="lg" className="gradient-purple border-0 text-primary-foreground font-semibold text-lg px-8 glow-purple">
                Genera il tuo QR Code
              </Button>
            </Link>
            <a href="#funzionalita">
              <Button size="lg" variant="outline" className="border-border text-foreground hover:bg-secondary text-lg px-8">
                Scopri di più
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* QR Types */}
      <section className="py-24 bg-background" id="tipi">
        <div className="container">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            QR Code per <span className="gradient-text">Tutto</span>
          </h2>
          <p className="text-center text-muted-foreground mb-16 max-w-xl mx-auto">
            Genera codici QR per URL, WiFi, email, telefono, SMS e testo libero.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {qrTypes.map((t) => (
              <Link
                key={t.label}
                to={`${SEO_ROUTES.generator}?type=${t.label.toLowerCase()}`}
                className="group flex flex-col items-center gap-3 rounded-xl border border-border bg-card p-6 transition-all hover:border-primary/50 hover:bg-secondary"
              >
                <div className="rounded-lg bg-primary/10 p-3 group-hover:bg-primary/20 transition-colors">
                  <t.icon className="h-6 w-6 text-primary" />
                </div>
                <span className="font-semibold text-foreground">{t.label}</span>
                <span className="text-xs text-muted-foreground text-center">{t.desc}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 bg-card" id="funzionalita">
        <div className="container">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16">
            Personalizzazione <span className="gradient-text">Incredibile</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {features.map((f) => (
              <div
                key={f.title}
                className="rounded-2xl border border-border bg-background p-8 transition-all hover:border-primary/40 hover:glow-purple"
              >
                <div className="rounded-xl bg-primary/10 p-4 w-fit mb-6">
                  <f.icon className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-foreground">{f.title}</h3>
                <p className="text-muted-foreground">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contenuto SEO: creare qr code gratis */}
      <section className="py-24 bg-card" id="creare-qr-code-gratis">
        <div className="container max-w-3xl">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Creare QR Code Gratis: guida rapida
          </h2>
          <div className="prose prose-neutral dark:prose-invert max-w-none text-muted-foreground space-y-4">
            <p>
              <strong>Creare un QR code gratis</strong> online è semplice: non serve registrazione né abbonamento. Con un generatore come QRGratis inserisci il contenuto (URL, WiFi, WhatsApp, testo, email, telefono o SMS), personalizzi colori e stile e scarichi il QR in PNG o SVG. Il risultato è un codice QR unico, leggibile da qualsiasi smartphone.
            </p>
            <p>
              I QR code sono codici a barre bidimensionali che contengono informazioni (link, testo, dati di rete). Inquadrandoli con la fotocamera o un’app lettore si accede subito al contenuto. Sono ideali per biglietti da visita, menu ristorante, WiFi, link a WhatsApp, PDF, profili social e molto altro. Un generatore QR code online gratis ti permette di creare qr code personalizzati senza limiti.
            </p>
            <p>
              Per <strong>creare qr code gratis</strong> scegli prima il tipo: URL per siti e link (es. Instagram, PDF, menu), WiFi per condividere la rete, SMS o telefono per contatti, email per messaggi precompilati, testo per informazioni libere. Poi inserisci i dati nei campi del generatore. Puoi modificare colori, forme dei moduli, angoli e aggiungere un logo al centro; la correzione errori alta (H) consente di inserire un’immagine senza compromettere la lettura.
            </p>
            <p>
              Dopo aver generato il QR puoi scaricarlo in PNG (per stampa e web) o SVG (per qualità vettoriale). Per la stampa usa una risoluzione adeguata (es. 300 DPI) e dimensioni sufficienti perché il codice sia leggibile a distanza. Testa sempre il QR con il telefono prima di stamparlo in grandi quantità. Nessun account è richiesto: creare qr code gratis è possibile in pochi secondi.
            </p>
            <p>
              Per casi d’uso specifici abbiamo guide dedicate: <Link to={SEO_ROUTES.whatsapp} className="link-on-card hover:underline">QR code WhatsApp</Link> per chat e gruppi, <Link to={SEO_ROUTES.wifi} className="link-on-card hover:underline">QR code WiFi</Link> per condividere la rete, <Link to={SEO_ROUTES.instagram} className="link-on-card hover:underline">QR code Instagram</Link> per profili e post, <Link to={SEO_ROUTES.pdf} className="link-on-card hover:underline">QR code PDF</Link> per documenti e <Link to={SEO_ROUTES.menuRistorante} className="link-on-card hover:underline">QR code menu ristorante</Link> per menu digitali. Leggi anche <Link to={SEO_ROUTES.cosEUnQrCode} className="link-on-card hover:underline">cos'è un QR code</Link>, <Link to={SEO_ROUTES.qrCodeDinamicoVsStatico} className="link-on-card hover:underline">QR code dinamico vs statico</Link>, <Link to={SEO_ROUTES.qrCodeConLogo} className="link-on-card hover:underline">come creare un QR code con logo</Link>, <Link to={SEO_ROUTES.qrCodeBigliettiDaVisita} className="link-on-card hover:underline">QR code per biglietti da visita</Link> e <Link to={SEO_ROUTES.qrCodeEventi} className="link-on-card hover:underline">QR code per eventi</Link>. Ogni guida include passi pratici e il link al generatore per creare qr code gratis subito.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-background">
        <div className="container text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Pronto a creare il tuo <span className="gradient-text">QR Code</span>?
          </h2>
          <p className="text-muted-foreground mb-10 max-w-lg mx-auto">
            Nessuna registrazione. Nessun limite. Inizia subito a generare QR code personalizzati.
          </p>
          <Link to={SEO_ROUTES.generator}>
            <Button size="lg" className="gradient-purple border-0 text-primary-foreground font-semibold text-lg px-10 glow-purple">
              Inizia Ora — È Gratis
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer con internal linking */}
      <footer className="border-t border-border py-12 bg-card">
        <div className="container">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-6">
            <div className="flex items-center gap-2">
              <QrCode className="h-5 w-5 text-primary" />
              <span className="font-bold gradient-text">QRGratis</span>
            </div>
            <nav className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm" aria-label="Link interni">
              <Link to={SEO_ROUTES.home} className="text-muted-foreground hover:text-primary transition-colors">
                Home
              </Link>
              <Link to={SEO_ROUTES.generator} className="text-muted-foreground hover:text-primary transition-colors">
                Creare QR Code Gratis
              </Link>
              <Link to={SEO_ROUTES.whatsapp} className="text-muted-foreground hover:text-primary transition-colors">
                QR Code WhatsApp
              </Link>
              <Link to={SEO_ROUTES.wifi} className="text-muted-foreground hover:text-primary transition-colors">
                QR Code WiFi
              </Link>
              <Link to={SEO_ROUTES.instagram} className="text-muted-foreground hover:text-primary transition-colors">
                QR Code Instagram
              </Link>
              <Link to={SEO_ROUTES.pdf} className="text-muted-foreground hover:text-primary transition-colors">
                QR Code PDF
              </Link>
              <Link to={SEO_ROUTES.menuRistorante} className="text-muted-foreground hover:text-primary transition-colors">
                QR Code Menu Ristorante
              </Link>
              <Link to={SEO_ROUTES.cosEUnQrCode} className="text-muted-foreground hover:text-primary transition-colors">
                Cos'è un QR Code
              </Link>
              <Link to={SEO_ROUTES.qrCodeDinamicoVsStatico} className="text-muted-foreground hover:text-primary transition-colors">
                QR Dinamico vs Statico
              </Link>
              <Link to={SEO_ROUTES.qrCodeConLogo} className="text-muted-foreground hover:text-primary transition-colors">
                QR Code con Logo
              </Link>
              <Link to={SEO_ROUTES.qrCodeBigliettiDaVisita} className="text-muted-foreground hover:text-primary transition-colors">
                QR Biglietti da Visita
              </Link>
              <Link to={SEO_ROUTES.qrCodeEventi} className="text-muted-foreground hover:text-primary transition-colors">
                QR per Eventi
              </Link>
            </nav>
          </div>
          <p className="text-sm text-muted-foreground text-center">
            © {new Date().getFullYear()} QRGratis — Generatore QR Code Online Gratuito. Crea QR code personalizzati senza registrazione.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Index;
